import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import GoalCard from '../../GlobalComponents/GoalCards';
import { getFacultyGoals } from '../../Utils/apiService';
import { getUserDetails } from '../../../src/storageService';
import KenLoader from '../../GlobalComponents/KenLoader';

type SubGoal = {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
};

type Goal = {
  id: string;
  title: string;
  desc: string;
  description: string;
  type: 'Group' | 'Individual';
  progress: { done: number; total: number };
  startdate: string;
  applicableTo?: string;
  audienceNameforfac?: string;
  enddate: string;
  isFaculty?: boolean;
  subgoals: SubGoal[];
};

const mapGoal = (g: any, isFaculty: boolean): Goal => ({
  id: g.milestone.Id,
  title: g.milestone.Name || '',
  desc: g.milestone.Description__c || '',
  applicableTo: g.applicableTo || '',
  audienceNameforfac: g.audienceNameforfac || '',
  description: g.milestone.Description__c || '',
  type:
    g.milestone.Type__c === 'Group'
      ? 'Group'
      : g.Type__c === 'Individual'
      ? 'Individual'
      : 'Group',
  progress: {
    done: parseInt(g.Progress__c || '0', 10),
    total: 100,
  },
  startdate: g.milestone.Start_Date__c || '',
  enddate: g.milestone.End_Date__c || '',
  isFaculty,
  subgoals: Array.isArray(g.subGoals)
    ? g.subGoals.map((sg: any) => ({
        id: sg.Id,
        name: sg.Name || '',
        description: sg.Description__c || '',
        startDate: sg.Start_Date__c || '',
        endDate: sg.EndDate__c || '',
      }))
    : [],
});

export const AdminGoals: React.FC = () => {
  const [adminGoals, setAdminGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminGoals = async () => {
      setLoading(true);
      try {
        const contactId = getUserDetails()?.ContactId;
        if (!contactId) return;

        const response = await getFacultyGoals(contactId);
        if (response.success && response.data?.adminGoals) {
          setAdminGoals(
            response.data.adminGoals.map((g: any) => mapGoal(g, false))
          );
        }
      } catch (err) {
        console.error('Error fetching admin goals:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAdminGoals();
  }, []);

  if (loading) return <KenLoader />;

  return (
    <Grid container spacing={2}>
      {adminGoals.length === 0 ? (
        <Typography sx={{ ml: 2 }}>No admin goals found.</Typography>
      ) : (
        adminGoals.map((g) => (
          <Grid item xs={12} sm={6} key={g.id}>
            <GoalCard {...g} />
          </Grid>
        ))
      )}
    </Grid>
  );
};

export const FacultyGoals: React.FC = () => {
  const [facultyGoals, setFacultyGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFacultyGoals = async () => {
      setLoading(true);
      try {
        const contactId = getUserDetails()?.ContactId;
        if (!contactId) return;

        const response = await getFacultyGoals(contactId);
        if (response.success && response.data?.facultyGoals) {
          setFacultyGoals(
            response.data.facultyGoals.map((g: any) => mapGoal(g, true))
          );
        }
      } catch (err) {
        console.error('Error fetching faculty goals:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFacultyGoals();
  }, []);

  if (loading) return <KenLoader />;

  return (
    <Grid container spacing={2}>
      {facultyGoals.length === 0 ? (
        <Typography sx={{ ml: 2 }}>No faculty goals found.</Typography>
      ) : (
        facultyGoals.map((g) => (
          <Grid item xs={12} sm={6} key={g.id}>
            <GoalCard {...g} />
          </Grid>
        ))
      )}
    </Grid>
  );
};
